import { inject, Injectable } from '@angular/core';
import { ApiAgentListTrmOrgGet$Params } from '@api/src/fn/agent/api-agent-list-trm-org-get';
import { ClientIdentificationResponse } from '@api/src/models/AgentApi/Contract/client-identification-response';
import { ListAvailTicketsRequest } from '@api/src/models/AgentApi/Contract/list-avail-tickets-request';
import { ListAvailTicketsResponse } from '@api/src/models/AgentApi/Contract/list-avail-tickets-response';
import { ListServiceTicketsRequest } from '@api/src/models/AgentApi/Contract/list-service-tickets-request';
import { ListServiceTicketsResponse } from '@api/src/models/AgentApi/Contract/list-service-tickets-response';
import { ListTrmOrgResponse } from '@api/src/models/AgentApi/Contract/list-trm-org-response';
import { AgentService } from '@api/src/services/agent.service';
import { ErrorResponse } from '@core/error/error-response.model';
import { StartWithUnit } from '@core/operators/start-with-unit';
import { RxState } from '@rx-angular/state';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { ClientState } from '@shared/state/client.state';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';


export enum PtFlagValue {
    NonPrintable = 0,
    Printable = 1,
}


export type ISelectTicketState = {
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    transportManagers: ListTrmOrgResponse[];
    services: ListServiceTicketsResponse[];
    tickets: ListAvailTicketsResponse[];
    trmId: number;
    srvId: number;
    ptFlag: PtFlagValue;
    mediumType: number;
    mediumSid: string;
}

export type SelectTicketAction = {
    requestServices: ListServiceTicketsResponse[];
    requestTransportManagers: ListTrmOrgResponse[];
    requestTickets: ListAvailTicketsResponse[];
    setTransportManagerId: number;
    setServiceId: number;
    setPtFlag: PtFlagValue;
    setMediumType: PtFlagValue;
}

@Injectable()
export class SelectTicketState extends RxState<ISelectTicketState> {
    readonly #actions: RxActions<SelectTicketAction> = new RxActionFactory<SelectTicketAction>().create();
    readonly #agentService: AgentService = inject(AgentService);
    readonly #clientState: ClientState = inject(ClientState);

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly services$: Observable<ListServiceTicketsResponse[]> = this.select('services');
    public readonly transportManagers$: Observable<ListTrmOrgResponse[]> = this.select('transportManagers');
    public readonly tickets$: Observable<ListAvailTicketsResponse[]> = this.select('tickets');


    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.hold(
            this.#clientState.client$.pipe(filter(Boolean)),
            (client: ClientIdentificationResponse) => {
                this.set({
                    mediumSid: client.mediumSid,
                    mediumType: client.mediumType,
                })
            },
        )
    }

    public requestTransportManagers(body?: ApiAgentListTrmOrgGet$Params): void {
        this.hold(
            this.#agentService.apiAgentListTrmOrgGet({ ...body, ptFlag: this.get('ptFlag') }),
            this.#actions.requestTransportManagers,
        );
    }

    public requestServices(body: ListServiceTicketsRequest): void {
        this.hold(
            this.#agentService.apiAgentListServiceTicketsPost({ body }),
            this.#actions.requestServices,
        );
    }

    public requestTickets(body: ListAvailTicketsRequest): void {
        this.hold(
            this.#agentService.apiAgentListAvailTicketsPost({ body }),
            this.#actions.requestTickets,
        );
    }

    public setTransportManagerId(trmId: number): void {
        this.#actions.setTransportManagerId(trmId);
    }

    public setServiceId(srvId: number): void {
        this.#actions.setServiceId(srvId);
    }

    public setPtFlag(ptFlag: PtFlagValue): void {
        this.#actions.setPtFlag(ptFlag);
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            errors: null,
            trmId: null,
            srvId: null,
            transportManagers: null,
            services: null,
            tickets: null,
            ptFlag: null,
        });
    }

    private connectSelectors(): void {
        this.connect('services', this.#actions.requestServices$);
        this.connect('ptFlag', this.#actions.setPtFlag$);
        this.connect('transportManagers', this.#actions.requestTransportManagers$);

        this.connect('srvId', this.#actions.setServiceId$.pipe(
            tap((srvId: number) => {
                this.set({ tickets: null });

                this.requestTickets({
                    trmId: this.get('trmId'),
                    srvId,
                    mediumType: this.get('mediumType'),
                    mediumSid: this.get('mediumSid'),
                    ptFlag: this.get('ptFlag'),
                });
            }),
        ));

        this.connect('trmId', this.#actions.setTransportManagerId$.pipe(
            tap((trmId: number) => {
                this.set({ services: null, tickets: null });
                this.requestServices({
                    trmId,
                    ptFlag: this.get('ptFlag'),
                    mediumType: this.get('mediumType'),
                    mediumSid: this.get('mediumSid'),
                })
            }),
        ));
        this.connect('tickets', this.#actions.requestTickets$);
    }
}
